import React, { Component, createRef, memo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Immutable from 'immutable';
import cn from 'classnames';
import { withTranslation } from 'react-i18next';

import Group from './controls/Group';
import FIELD_TYPES from '../../../../configs/fieldTypes';
import ControlItem from './ControlItem';
import calcVisibleControls from '../../../../utils/calcVisibleControls';
import FieldApi from '../../../../models/FieldApi/index';

import styles from './controlList.less';
import ControlItemMassUpdate from '../../../RecordsBatch/RecordsBatchUpdate/RecordsBatchUpdateForm/ControlItemMassUpdate';
import { Control } from './Control';

const DefaultWrap = ({ children }) => children;

// beacuse Symbol can't convert to String(error from React)
const virtualGroupId = '$virtualGroupId';

let idPrefix = 0;

// Если у тебя есть необходимость работать с типом связанной записи, то лучше использовать обертку для пребразования пропсов RecordFields!
// Которая сама смапит значения связанной записи

/**
 * ControlList Component
 *
 * This component renders a list of controls (fields) based on configuration and values provided.
 * It handles sections, visibility of controls, and events like changes and hiding.
 *
 * @component
 */
class ControlList extends Component {
  /**
   * Prop types validation.
   */
  static propTypes = {
    /**
     * The data that contains the configurations and values for controls.
     */
    data: PropTypes.object.isRequired,

    /**
     * Callback function for handling change events of controls.
     */
    onChange: PropTypes.func,

    /**
     * Callback function for handling visibility changes of controls.
     */
    onHiddenChange: PropTypes.func,

    /**
     * Callback function for handling the end of editing on a control.
     */
    onEndEditing: PropTypes.func,

    /**
     * A key to store and retrieve the visibility state of sections from localStorage.
     */
    keyForStorage: PropTypes.string,

    /**
     * Editable status of the fields, determining whether they are editable or not.
     */
    fieldsEditableStatus: PropTypes.object,
  };

  ref = createRef();

  key = localStorage.getItem(`controlList.${this.props.keyForStorage}`);

  state = {
    closed: {},
    sections: [],
  };

  idPrefix = idPrefix++;

  setSections(configs, values, tabId) {
    let _curGroup;
    let _curTabId;
    let sections = [];
    const closed = {};

    configs.forEach((config) => {
      if (config.get('type') === FIELD_TYPES.GROUP) {
        if (config.getIn(['config', 'tab'])) {
          _curTabId = config.get('id');
        }
        _curGroup = {
          id: config.get('id'),
          tabId: _curTabId,
          config,
          controls: [],
          values: {},
        };
        sections.push(_curGroup);

        if (config.getIn(['config', 'collapsed'])) {
          closed[_curGroup.id] = true;
        } else if (config.getIn(['config', 'collapsed']) === false) {
          closed[_curGroup.id] = false;
        }
      } else {
        if (!_curGroup) {
          _curGroup = {
            id: virtualGroupId,
            virtual: true,
            tabId: _curTabId,
            config: Immutable.fromJS({ name: '', type: FIELD_TYPES.GROUP }),
            controls: [],
            values: {},
          };
          sections.push(_curGroup);
        }
        if (config.get('error')) {
          closed[_curGroup.id] = false;
        }
        _curGroup.controls.push(config);
      }
    });
    // filter by tab
    if (tabId && tabId > 0) {
      sections = sections.filter((s) => s.tabId == tabId);
    }
    this.setState({
      sections,
      closed: {
        ...this.state.closed,
        ...closed,
      },
    });
  }

  toggleList = (sectionId) => {
    const closedSections = {
      ...this.state.closed,
      [sectionId]: !this.state.closed[sectionId],
    };

    this.setState({
      closed: closedSections,
    });

    if (this.props.keyForStorage) {
      localStorage.setItem(`controlList.${this.props.keyForStorage}`, JSON.stringify(closedSections));
    }
  };

  componentDidUpdate() {
    if (this.errorControl && this.errorControl !== this.lastErrorControl) {
      this.errorControl.focus();
      this.lastErrorControl = this.errorControl;
    }
  }

  componentDidMount() {
    const { configs } = this.props.data;
    const { values } = this.props.data;
    const { tabId } = this.props;
    this.setSections(configs, values, tabId);
    this.setHiddenFields(configs, values);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const newConfig = nextProps.data.configs;
    const { configs } = this.props.data;

    const newValues = nextProps.data.values;
    const { values } = this.props.data;

    const newTabId = nextProps.tabId;
    const { tabId } = this.props;

    if ((newConfig && newConfig !== configs) || (newValues && newValues !== values) || newTabId !== tabId) {
      this.setSections(newConfig, newValues, newTabId);
      this.setHiddenFields(newConfig, newValues);
    }
  }

  setHiddenFields(configs, values) {
    const visible = calcVisibleControls(values, configs);
    const hidden = {};
    _.mapKeys(visible, (v, fId) => {
      if (!v) {
        hidden[fId] = !v;
      }
    });
    const oldHidden = this.state.hidden;
    if (!_.isEqual(hidden, oldHidden)) {
      this.setState({ hidden });
      this.props.onHiddenChange && this.props.onHiddenChange(hidden);
    }
  }

  getControllVisibility = (fieldId) => {
    const { fieldsEditableStatus } = this.props;
    const fieldEditableStatus = fieldsEditableStatus && fieldsEditableStatus.get(fieldId);

    return _.isUndefined(fieldEditableStatus) ? false : fieldEditableStatus;
  };

  render() {
    const { props, state } = this;
    const { data, params, className, compact, sceneId, labelOnTop, countingFieldsChanged, onChange, onEndEditing, t } =
      props;
    const { values, configs } = data;
    const { hidden } = state;
    const firstError = configs.find((f) => f.get('error'));

    return (
      <div
        ref={this.ref}
        className={cn(styles.controlList, className, {
          [styles.labelOnTop]: labelOnTop,
        })}
      >
        {state.sections.map((section) => {
          // hide hidden sections by field config
          if (section.config.get('hidden')) {
            return null;
          }

          // hide hidden sections by visibility
          if (section.id && !section.virtual && hidden[section.id]) {
            return null;
          }

          // hide empty sections
          const hasVisibleControls = !!section.controls.find((control) => !hidden[control.get('id')]);
          if (!hasVisibleControls) {
            return null;
          }

          const sectionVirtual = section.virtual;
          const sectionClosed = state.closed[section.id];
          const singleFieldInSection = section.controls?.length === 1;
          const singleFieldInTab = state.sections.length === 1 && singleFieldInSection && !sectionVirtual; // TODO: сделать для связ каталогов свой режим(Ильяс)
          return (
            <div
              className={cn({
                [styles.sectionContentSingleFieldInTab]: singleFieldInTab,
              })}
              key={section.id}
            >
              {!sectionVirtual ? (
                <Group
                  params={params}
                  id={section.id}
                  title={section.config.get('name')}
                  subTitle={section.config.get('subTitle')}
                  closed={sectionClosed}
                  labelOnTop={labelOnTop}
                  onClick={this.toggleList}
                  count={t('record.groupFields.count', {
                    count: section.controls.length,
                  })}
                />
              ) : null}

              <div
                className={cn(styles.sectionFields, {
                  [styles.sectionFieldsHidden]: sectionClosed,
                  [styles.sectionFieldsSingleFieldInSection]: singleFieldInSection,
                  [styles.sectionFieldsSingleFieldInTab]: singleFieldInTab,
                })}
              >
                {section.controls.map((controlConfig) => {
                  const config = controlConfig.get('config');
                  const controlId = controlConfig.get('id');

                  // hide hidden fields by field config
                  if (controlConfig.get('hidden')) {
                    return null;
                  }

                  // hide hidden fields by visibility
                  if (hidden[controlId]) {
                    return null;
                  }

                  const type = controlConfig.get('type');

                  // Возможность добавлять кастомные поля в контрол лист, например, редактор формул
                  const CustomControl = controlConfig.get('component');

                  const currentControlError = firstError && firstError.get('id') === controlId;
                  const htmlId = this.idPrefix + controlId;

                  const eventable = controlConfig.get('eventable');
                  const formulaConfig = controlConfig.get('formulaConfig');
                  const formula = formulaConfig ? formulaConfig?.get('formula') : null;
                  const formulaType = controlConfig.get('formulaType');
                  const isHaveFormula = !!formula && !!formulaType;
                  const isReadonly = controlConfig.get('apiOnly') || controlConfig.get('readOnly');
                  const editable = !(isReadonly || isHaveFormula);

                  const value = values.get(controlId);
                  const hasValue =
                    value && value.get ? !!value.size : value !== undefined && value !== null && value !== '';

                  const ControlProps = {
                    id: controlId,
                    containerRef: this.ref,
                    sceneId,
                    type,
                    htmlId,
                    value,
                    controlConfig,
                    config,
                    params,
                    editable,
                    formula,
                    t,
                    required: controlConfig.get('required'),
                    hint: controlConfig.get('hint'),
                    eventable,
                    apiOnly: controlConfig.get('apiOnly'),
                    readOnly: controlConfig.get('readOnly'),
                    updateProcess: controlConfig.get('updateProcess'),
                    error: controlConfig.get('error'),
                    onChange: (value) => onChange(controlId, value),
                    onEndEditing: (value) => onEndEditing(controlId, value),
                  };

                  // canExpandWidth, canExpandWidthAndHeight
                  const extraProps = CustomControl
                    ? {}
                    : FieldApi.getCanComponentExpandWidthOrHeight(
                        controlConfig,
                        singleFieldInSection,
                        singleFieldInTab,
                      );

                  const ControlWrap = props.ControlWrap || DefaultWrap;

                  return (
                    <ControlItem
                      sceneId={sceneId}
                      fieldId={controlId}
                      labelRef={currentControlError && ((node) => (this.errorControl = node))}
                      key={controlId}
                      htmlId={htmlId}
                      config={config}
                      name={controlConfig.get('name')}
                      required={controlConfig.get('required')}
                      type={type}
                      apiOnly={controlConfig.get('apiOnly')}
                      readOnly={controlConfig.get('readOnly')}
                      hint={controlConfig.get('hint')}
                      updateProcess={controlConfig.get('updateProcess')}
                      error={controlConfig.get('error')}
                      hasValue={hasValue}
                      editable={editable}
                      formula={formula}
                      compact={compact}
                      labelOnTop={labelOnTop}
                      {...(extraProps || {})}
                    >
                      {countingFieldsChanged ? (
                        <ControlItemMassUpdate {...props} controlConfig={controlConfig}>
                          <ControlWrap ControlProps={ControlProps} hasValue={hasValue}>
                            <Control
                              controlConfig={controlConfig}
                              CustomControl={CustomControl}
                              allProps={props}
                              ControlProps={ControlProps}
                            />
                          </ControlWrap>
                        </ControlItemMassUpdate>
                      ) : (
                        <ControlWrap ControlProps={ControlProps} hasValue={hasValue}>
                          <Control
                            controlConfig={controlConfig}
                            CustomControl={CustomControl}
                            allProps={props}
                            ControlProps={ControlProps}
                            extraProps={extraProps}
                          />
                        </ControlWrap>
                      )}
                    </ControlItem>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}

export default withTranslation()(memo(ControlList));
